import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";

import { api, setCsrfToken, type User } from "@/lib/api";

export function useSession() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<User>("/api/auth/me"),
    retry: false,
    staleTime: 60_000,
  });

  // mint CSRF once we know who we are
  useEffect(() => {
    if (!data) return;
    api.get<{ csrf_token: string }>("/api/auth/csrf")
      .then((r) => setCsrfToken(r.csrf_token))
      .catch(() => undefined);
  }, [data]);

  // bounce to login when any call 401s
  useEffect(() => {
    const f = () => qc.setQueryData(["me"], null);
    window.addEventListener("nx:unauthorized", f);
    return () => window.removeEventListener("nx:unauthorized", f);
  }, [qc]);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      setCsrfToken("");
      qc.setQueryData(["me"], null);
      qc.clear();
    }
  }, [qc]);

  return {
    user: data ?? null,
    loading: isLoading,
    error,
    logout,
    refetch: () => qc.invalidateQueries({ queryKey: ["me"] }),
  };
}
